import MobileSettingsMenu from "./mobile_settings_menu";
import SubtitleManager from '../subtitle_manager';
import { BitrateManager } from '@javascripts/bitrate_manager';
import ConnectionAdapter from '@javascripts/adapters/connection_adapter';
import { PlayerAhoyCommonEvent } from '@javascripts/player_ahoy_common_event';
import FullscreenApiAdapter from '@javascripts/adapters/fullscreen_api_adapter';
import i18n from '@javascripts/utils/i18n';
import isFeatureToggleOn from "@javascripts/utils/feature_toggle";

export default class MobileSettingsBottomsheet {
  activeSubtitle = "";
  activeQuality = "";
  activePlaybackRates = "";
  subtitleManager = null;
  bitrateManager = null;

  constructor(player, adapter, options = {}) {
    this._el = document.createElement('div');
    this._player = player;
    this._adapter = adapter;
    this._options = options;
    
    this.menu = null;

    this.bindOnLoadedMetadata(adapter);
  }

  get options() {
    return this._options;
  }

  get adapter() {
    return this._adapter;
  }

  get player() {
    return this._player;
  }

  el() {
    return this._el;
  }

  updateActiveQuality() {
    this.activeQuality = this.bitrateManager.getActiveQuality();
  }

  updateActiveSubtitle() {
    this.activeSubtitle = this.subtitleManager.getActiveSubtitle().label;
  }

  updateActivePlaybackRates() {
    let rate = "Normal"
    if(this.player.playbackRate() !== 1) {
      rate = this.player.playbackRate() +"x"
    } 
    this.activePlaybackRates = rate;
  }

  build() {
    this.el().className = 'vjs-settings-button__bottomsheet';
    this.setDisplayTypeAttr(this.el());
    const overlay = document.createElement('div');
    overlay.className = "vjs-settings-button__bottomsheet__overlay";
    overlay.onclick = () => {
      this.changeMenu(i18n("settings_menu.settings"));
      this.hide();
    }
    this.el().appendChild(overlay);
    this.el().appendChild(this.menu.el());
  }

  setDisplayTypeAttr(element) {
    if(this.options.displayType !== undefined && this.options.displayType !== null) {
      element.setAttribute("data-display-type",this.options.displayType);
    } else {
      element.setAttribute("data-display-type","default");
    }
  }

  getDisplayTypeAttr() {
    if(this.options.displayType !== undefined && this.options.displayType !== null) {
      return this.options.displayType;
    } else {
      return "default";
    }
  }

  show() {
    this.el().style.display = "flex";
    this.menu.show();
  }

  hide() {
    this.el().style.display = "none";
  }

  displayStatus() {
    return getComputedStyle(this.el()).display;
  }

  toggle(){
    if (this.displayStatus() == "none") {
      this.show();
    }else{
      this.hide();
    }
  }

  initMobileVideoQualityMenu(onSelectQualityCallback) {
    let videoQualities = []
    const videoQualityMenu = new MobileSettingsMenu({
      title: i18n("settings_menu.video_quality"),
      showBackButton: true,
      onClickBack: this.handleBack.bind(this),
      menuItems: videoQualities
    });

    videoQualities = this.bitrateManager.getResolutions()
      .map((quality) => {
        return {
          menuType: quality.label === 'Auto'? 'checked' : undefined,
          label: quality.label,
          onClick: () => {
            quality.enableQuality()
            setTimeout(() => {
              onSelectQualityCallback('select')
            }, 100)
          }
        }
      })

    videoQualityMenu.updateList(videoQualities);
    return videoQualityMenu;
  }

  initMobileSubtitleMenu(player) {
    let subtitles = [];
    const subtitleMenu = new MobileSettingsMenu({
      title: i18n("settings_menu.subtitle"),
      showBackButton: true,
      onClickBack: this.handleBack.bind(this),
      menuItems: subtitles
    })
  
    const triggerSubtitleChangeEvent = (selectedTrack) => {
      player.trigger({type: "vp-subtitle-change", id: this.options.videoId, track: selectedTrack});
    }
    const sendSubtitleEvent = (action, label) => {
      if (!this.subtitleManager) return;
  
      const params = {
        action: action,
        subtitle: label,
        video_id: this.options.videoId,
        play_uuid: this.options.play_uuid
      }
  
      PlayerAhoyCommonEvent.sendSubtitle(params);
    }
    subtitles = this.subtitleManager.getSubtitleMenu().map((subtitle) => {
      return {
        label: subtitle.label,
        menuType: subtitle.active ? 'checked' : undefined,
        onClick: () => {
          subtitle.onClick();
          triggerSubtitleChangeEvent(subtitle.label);
          sendSubtitleEvent('select', subtitle.label);
        }
      }
    });
    subtitleMenu.updateList(subtitles);
    return subtitleMenu;
  }

  initMobileSettingMenu(activeQuality, activeSubtitle, activePlaybackRates) {
    const triggerSubtitleClickEvent = () => {
      this.player.trigger({type: "vp-subtitle-click", id: this.options.videoId});
    }
    const settingMenu = new MobileSettingsMenu({
      title: i18n("settings_menu.settings"),
      showBackButton: true,
      onClickBack: () => {
        this.hide()
      },
      menuItems: [
        {
          label: i18n("settings_menu.video_quality"),
          activeLabel: activeQuality,
          enabled: true,
          menuType: "uncheckable",
          onClick: () => {
            this.changeMenu(i18n("settings_menu.video_quality"));
            this.sendBitrateEvent('open');
            PlayerAhoyCommonEvent.sendPlaybackBitrateShow(this.options.play_uuid);
          }
        },
        {
          label: i18n("settings_menu.subtitle"),
          activeLabel: activeSubtitle,
          enabled: this.subtitleManager.textTracks().length > 0,
          menuType: "uncheckable",
          onClick: () => {
            this.changeMenu(i18n("settings_menu.subtitle"));
            triggerSubtitleClickEvent();
          }
        },
        {
          label: i18n("settings_menu.playback_speed"),
          activeLabel: activePlaybackRates,
          enabled: this.playbackSpeedMenuIsEnabled(),
          menuType: "uncheckable",
          onClick: () => {
            this.changeMenu(i18n("settings_menu.playback_speed"));
          }
        },
        {
          label: i18n("settings_menu.send_feedback"),
          enabled: true,
          menuType: "uncheckable",
          onClick: () => {
            this.hide();
            FullscreenApiAdapter.exitFullscreen();
            this.publishSendFeedbackTopic();
          }
        }
      ]
    })
    return settingMenu
  }

  initMobilePlaybackSpeedMenu(player) {
    const playbackRates = [
      {
        label: "0.5x",
        menuType: player.playbackRate() === 0.5 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(0.5)
        }
      },
      {
        label: "0.75x",
        menuType: player.playbackRate() === 0.75 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(0.75)
        }
      },
      {
        label: "Normal",
        menuType: player.playbackRate() === 1 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(1)
        }
      },
      {
        label: "1.25x",
        menuType: player.playbackRate() === 1.25 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(1.25)
        }
      },
      {
        label: "1.5x",
        menuType: player.playbackRate() === 1.5 ? 'checked' : undefined,
        onClick: () => {
          player.playbackRate(1.5)
        }
      }
    ]
    
    const playbackRatesMenu = new MobileSettingsMenu({
      title: i18n("settings_menu.playback_speed"),
      showBackButton: true,
      onClickBack: this.handleBack.bind(this),
      menuItems: []
    })

    playbackRatesMenu.updateList(playbackRates)

    return playbackRatesMenu
  }

  publishSendFeedbackTopic() {
    window.Topic.publish('watchpage/send_feedback/show', {
      issue_category_code: 'icc4',
    })
  }

  handleBack() {
    this.changeMenu(i18n("settings_menu.settings"));
  }

  changeMenu(menuName) {
    if (menuName === i18n("settings_menu.subtitle")) {
      this.el().replaceChild(this.subtitleMenu.el(), this.menu.el());
      this.menu = this.subtitleMenu;
    } else if(menuName === i18n("settings_menu.video_quality")) {
      this.el().replaceChild(this.qualityMenu.el(), this.menu.el());
      this.menu = this.qualityMenu;
    } else if(menuName === i18n("settings_menu.playback_speed")) {
      this.el().replaceChild(this.playbackRatesMenu.el(), this.menu.el());
      this.menu = this.playbackRatesMenu;
    } else {
      this.updateActiveQuality();
      this.updateActiveSubtitle();
      this.updateActivePlaybackRates();
      this.el().replaceChild(this.settingMenu.el(), this.menu.el());
      const newItems = this.settingMenu.menuItems.map((item) => 
        {
          const newItem = item;
          if(item.label === i18n("settings_menu.video_quality")) {
            newItem.activeLabel = this.activeQuality;
          } else if (item.label === i18n("settings_menu.subtitle")) {
            newItem.activeLabel = this.activeSubtitle;
          } else if (item.label === i18n("settings_menu.playback_speed")) {
            newItem.activeLabel = this.activePlaybackRates;
          }
          return newItem;
        }
      );
      this.settingMenu.updateList(newItems);
      this.menu = this.settingMenu;
    }
    this.menu.show();
  }

  sendBitrateEvent(action) {
    const attr = this.adapter.bitratesObject().getCurrentMediaAttribute();
    const params = {
      play_uuid: this.options.play_uuid,
      action: action,
      quality: window['playback_selected_quality']?.[this.adapter.id()] || null,
      bandwidth: attr.bitrate,
      network_type: ConnectionAdapter.effectiveType(),
      content_type: this.options.type
    }
    PlayerAhoyCommonEvent.sendBitrate(params);
  }

  playbackSpeedMenuIsEnabled(){
    return isFeatureToggleOn('enable_playback_speed') && (this.options.type != 'Livestreaming')
  }

  bindOnLoadedMetadata(adapter){
    this.player.on("loadedmetadata", () => {
      
      this.subtitleManager = new SubtitleManager(this.player, this.options);
      this.subtitleManager.setPreferredSub();
      this.activeSubtitle = this.subtitleManager.getActiveSubtitle().label;
      this.bitrateManager = new BitrateManager(adapter, this.options.resolutionMapping);
      this.activeQuality = this.bitrateManager.getActiveQuality();

      if(this.playbackSpeedMenuIsEnabled()) {
        this.playbackRatesMenu = this.initMobilePlaybackSpeedMenu(
          this.player
        )
        this.updateActivePlaybackRates()
      }

      this.settingMenu = this.initMobileSettingMenu( 
        this.activeQuality, 
        this.activeSubtitle,
        this.activePlaybackRates);
      this.subtitleMenu = this.initMobileSubtitleMenu(
        this.player
      );
      this.qualityMenu = this.initMobileVideoQualityMenu(
        this.sendBitrateEvent.bind(this)
      );

      this.menu = this.settingMenu;

      this.build();
      this.hide();
    });
  }
}